// Two-click date selection on the calendar grid.
// A mouse press anchors a cell. Moving the pointer paints the range,
// and the next click opens the add-booking form.
// A touch scrolls. A tap opens the add-booking form for that date.
// Holding still, then dragging, paints a range and opens the form when the
// finger lifts on a free range. A hold that does not move waits for a second tap.

const TOUCH_MOVE_THRESHOLD = 8;
const TOUCH_HOLD_MS = 400;

function canSelectDates(permission) {
  return ['Owner', 'Admin', 'Edit'].includes(permission);
}

export const calendarDateSelectMethods = {
  dateSelect: null,
  dateSelectHint: false,

  handleDateSelectPointerDown(event) {
    if (event.button !== 0) return;
    if (!canSelectDates(this.userPermission)) return;
    if (this.bookingDrag && this.bookingDrag.active) return;

    const cell =
      event.currentTarget?.closest('.calendar-date') ||
      event.target?.closest('.calendar-date');
    const info = this._readDateCell(cell);
    if (!info?.date || !info.productId) return;

    if (event.pointerType === 'touch') {
      this._armDateSelectHold(event, info, cell);
      return;
    }

    if (this.dateSelect) return;

    this._beginDateSelect(info);
    this._trackDateSelectPress(event, true);
  },

  _armDateSelectHold(event, info, cell) {
    this._clearDateSelectHold();
    const hold = {
      info,
      cell,
      startX: event.clientX,
      startY: event.clientY,
      pointerId: event.pointerId,
      startedAt: performance.now(),
      moved: false,
      armed: false,
      finished: false,
      frame: null,
    };
    const fingerMoved = (x, y) =>
      Math.abs(x - hold.startX) >= TOUCH_MOVE_THRESHOLD ||
      Math.abs(y - hold.startY) >= TOUCH_MOVE_THRESHOLD;
    hold.tryArm = () => {
      if (hold.armed || hold.finished || hold.moved) return false;
      if (performance.now() - hold.startedAt < TOUCH_HOLD_MS) return false;
      this._startTouchDateSelect(hold);
      return hold.armed;
    };
    hold.onTouchMove = (touchEvent) => {
      const point = touchEvent.touches && touchEvent.touches[0];
      if (!point || hold.finished) return;
      if (!hold.armed) {
        const elapsed = performance.now() - hold.startedAt;
        if (
          fingerMoved(point.clientX, point.clientY) &&
          elapsed < TOUCH_HOLD_MS
        ) {
          hold.moved = true;
          this._stopDateSelectHoldFrame(hold);
          return;
        }
        if (!hold.tryArm()) return;
      }
      if (touchEvent.cancelable) touchEvent.preventDefault();
      this._paintDateSelectAt(point.clientX, point.clientY);
    };
    hold.onUp = (upEvent) => {
      if (upEvent.pointerId !== hold.pointerId) return;
      if (upEvent.type === 'pointercancel') return;
      this._finishDateSelectHold(hold, false);
    };
    hold.onTouchEnd = (touchEvent) => {
      this._finishDateSelectHold(hold, touchEvent.type === 'touchcancel');
    };
    const tick = (now) => {
      if (hold.finished || hold.moved || this._dateSelectHold !== hold) return;
      if (now - hold.startedAt >= TOUCH_HOLD_MS) {
        hold.tryArm();
        return;
      }
      hold.frame = window.requestAnimationFrame(tick);
    };
    this._dateSelectHold = hold;
    this._dateSelectTouchEnd = hold.onTouchEnd;
    hold.frame = window.requestAnimationFrame(tick);
    window.addEventListener('pointerup', hold.onUp);
    window.addEventListener('pointercancel', hold.onUp);
    window.addEventListener('touchmove', hold.onTouchMove, { passive: false });
    window.addEventListener('touchend', hold.onTouchEnd);
    window.addEventListener('touchcancel', hold.onTouchEnd);
  },

  _paintDateSelectAt(x, y) {
    this._moveDateSelect({
      pointerType: 'touch',
      clientX: x,
      clientY: y,
      cancelable: false,
    });
  },

  _finishDateSelectHold(hold, cancelled) {
    if (hold.finished) return;
    if (!hold.armed && !hold.moved && !cancelled) hold.tryArm();
    hold.finished = true;
    if (hold.armed) {
      this._finishDateSelectPress({
        type: cancelled ? 'pointercancel' : 'pointerup',
        pointerType: 'touch',
      });
      this._clearDateSelectTouchEnd();
      return;
    }
    const moved = hold.moved;
    const hadSelection = Boolean(this.dateSelect);
    const tappedCell = hold.cell;
    this._clearDateSelectHold();
    if (cancelled || moved) {
      this._swallowNextDateSelectClick();
      return;
    }
    if (hadSelection) return;
    this._swallowNextDateSelectClick();
    window.Alpine.store('addBookingForm').handleCalendarDateClick({
      target: tappedCell,
      currentTarget: tappedCell,
    });
  },

  _stopDateSelectHoldFrame(hold) {
    if (!hold || !hold.frame) return;
    window.cancelAnimationFrame(hold.frame);
    hold.frame = null;
  },

  _startTouchDateSelect(hold) {
    if (hold.moved || hold.armed) return;
    const onTouchMove = hold.onTouchMove;
    const info = hold.info;
    const startX = hold.startX;
    const startY = hold.startY;
    hold.armed = true;
    this._stopDateSelectHoldFrame(hold);
    window.removeEventListener('pointerup', hold.onUp);
    window.removeEventListener('pointercancel', hold.onUp);
    this._dateSelectHold = null;
    this._dateSelectTouchMove = onTouchMove;
    this._keepingTouchMove = true;
    this._beginDateSelect(info);
    this._keepingTouchMove = false;
    this._dateSelectTouchArmed = true;
    this._trackDateSelectPress(
      { pointerType: 'touch', clientX: startX, clientY: startY },
      true,
    );
    if (this._dateSelectPress) this._dateSelectPress.held = true;
    document.body.classList.add('calendar-date-select-lock');
  },

  _clearDateSelectTouchEnd() {
    if (this._keepingTouchMove) return;
    if (!this._dateSelectTouchEnd) return;
    window.removeEventListener('touchend', this._dateSelectTouchEnd);
    window.removeEventListener('touchcancel', this._dateSelectTouchEnd);
    this._dateSelectTouchEnd = null;
  },

  _clearDateSelectHold() {
    const hold = this._dateSelectHold;
    if (!hold) return;
    this._stopDateSelectHoldFrame(hold);
    window.removeEventListener('pointerup', hold.onUp);
    window.removeEventListener('pointercancel', hold.onUp);
    window.removeEventListener('touchmove', hold.onTouchMove);
    this._clearDateSelectTouchEnd();
    this._dateSelectHold = null;
  },

  _clearDateSelectTouchMove() {
    if (this._keepingTouchMove) return;
    if (this._dateSelectTouchMove) {
      window.removeEventListener('touchmove', this._dateSelectTouchMove);
      this._dateSelectTouchMove = null;
    }
    this._dateSelectTouchArmed = false;
  },

  _swallowNextDateSelectClick() {
    if (this._swallowDateSelectClick) {
      window.removeEventListener('click', this._swallowDateSelectClick, true);
    }
    this._swallowDateSelectClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      window.removeEventListener('click', this._swallowDateSelectClick, true);
      this._swallowDateSelectClick = null;
    };
    window.addEventListener('click', this._swallowDateSelectClick, true);
    this._suppressDateSelectClick = true;
    window.setTimeout(() => {
      this._suppressDateSelectClick = false;
    }, 400);
  },

  _trackDateSelectPress(event, suppressClick) {
    if (this._dateSelectPressUp) {
      window.removeEventListener('pointerup', this._dateSelectPressUp);
      window.removeEventListener('pointercancel', this._dateSelectPressUp);
    }
    this._dateSelectPress = {
      touch: event.pointerType === 'touch',
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      suppressClick,
    };
    this._dateSelectPressUp = (upEvent) => this._finishDateSelectPress(upEvent);
    window.addEventListener('pointerup', this._dateSelectPressUp);
    window.addEventListener('pointercancel', this._dateSelectPressUp);
  },

  handleDateSelectClick(event) {
    if (this._suppressDateSelectClick) return;
    if (!canSelectDates(this.userPermission)) return;
    if (this.bookingDrag && this.bookingDrag.active) return;

    const cell =
      event.currentTarget?.closest('.calendar-date') ||
      event.target?.closest('.calendar-date');
    const info = this._readDateCell(cell);
    if (!info?.date || !info.productId) return;

    if (!this.dateSelect) {
      this._beginDateSelect(info);
      return;
    }

    if (String(this.dateSelect.productId) !== String(info.productId)) {
      const showHint = this.dateSelectHint;
      this.cancelDateSelect();
      this._beginDateSelect(info);
      this.dateSelectHint = showHint;
      return;
    }

    this._applyDateSelectHover(info);
    this._confirmOrCancelDateSelect();
  },

  _finishDateSelectPress(event) {
    window.removeEventListener('pointerup', this._dateSelectPressUp);
    window.removeEventListener('pointercancel', this._dateSelectPressUp);
    this._dateSelectPressUp = null;
    const press = this._dateSelectPress;
    this._dateSelectPress = null;
    const suppressClick = () => {
      this._suppressDateSelectClick = true;
      window.setTimeout(() => {
        this._suppressDateSelectClick = false;
      }, 0);
    };
    if (!this.dateSelect) return;
    if (event.type === 'pointercancel') {
      suppressClick();
      this.cancelDateSelect();
      return;
    }
    if (press && press.touch && press.moved) {
      this._swallowNextDateSelectClick();
      this._confirmOrCancelDateSelect();
      return;
    }
    if (press && press.suppressClick) {
      this._clearDateSelectTouchMove();
      document.body.classList.remove('calendar-date-select-lock');
      this._swallowNextDateSelectClick();
      this.dateSelectHint = Boolean(press.touch);
    }
  },

  _confirmOrCancelDateSelect() {
    if (!this.dateSelect) return;
    if (this.dateSelect.invalid) {
      this.cancelDateSelect();
      return;
    }
    const snapshot = this._dateSelectSnapshot();
    this.cancelDateSelect();
    window.Alpine.store('addBookingForm').handleCalendarDateClick(snapshot);
  },

  cancelDateSelect() {
    if (this._dateSelectMove) {
      window.removeEventListener('pointermove', this._dateSelectMove);
      this._dateSelectMove = null;
    }
    if (this._dateSelectKey) {
      window.removeEventListener('keydown', this._dateSelectKey);
      this._dateSelectKey = null;
    }
    if (this._dateSelectDocClick) {
      document.removeEventListener('click', this._dateSelectDocClick, true);
      this._dateSelectDocClick = null;
    }
    if (this._dateSelectPressUp) {
      window.removeEventListener('pointerup', this._dateSelectPressUp);
      window.removeEventListener('pointercancel', this._dateSelectPressUp);
      this._dateSelectPressUp = null;
    }
    this._clearDateSelectHold();
    this._clearDateSelectTouchMove();
    this._clearDateSelectTouchEnd();
    document.body.classList.remove('calendar-date-selecting');
    document.body.classList.remove('calendar-date-select-lock');
    this.dateSelect = null;
    this.dateSelectHint = false;
    this._dateSelectPress = null;
  },

  dateSelectShowsAnchorDay(productId, unitId, departureId, date) {
    const sel = this.dateSelect;
    if (
      !sel ||
      sel.anchorDate !== date ||
      String(sel.productId) !== String(productId)
    ) {
      return false;
    }
    if (sel.scheduled) {
      if (String(sel.departureId || '') !== String(departureId || ''))
        return false;
      if (sel.anchorUnitId && String(sel.anchorUnitId) !== String(unitId || ''))
        return false;
    } else if (String(sel.anchorUnitId || '') !== String(unitId || '')) {
      return false;
    }
    return true;
  },

  dateSelectClass(productId, unitId, departureId, date) {
    const sel = this.dateSelect;
    if (!sel || String(sel.productId) !== String(productId) || !date) return '';
    if (!this._paintedDates(sel).includes(date)) return '';

    if (sel.scheduled) {
      if (String(sel.departureId || '') !== String(departureId || ''))
        return '';
      if (
        sel.anchorUnitId &&
        String(sel.anchorUnitId) !== String(unitId || '')
      ) {
        return '';
      }
    } else if (!this._selectedUnits(sel).includes(String(unitId))) {
      return '';
    }

    const paint = sel.invalid ? 'date-select-invalid' : 'date-select-valid';
    if (sel.anchorDate === sel.hoverDate && date === sel.anchorDate) {
      return `${paint} date-select-armed`;
    }
    return paint;
  },

  _beginDateSelect(info) {
    this.cancelDateSelect();
    this.dateSelect = {
      productId: info.productId,
      productType: info.productType,
      scheduled: Boolean(info.departureId),
      anchorDate: info.date,
      hoverDate: info.date,
      anchorUnitId: info.unitId,
      hoverUnitId: info.unitId,
      departureId: info.departureId,
      departureTime: info.departureTime,
      rateUnitId: info.rateUnitId,
      invalid: false,
    };
    this._refreshDateSelectValidity();
    this._dateSelectMove = (moveEvent) => this._moveDateSelect(moveEvent);
    this._dateSelectKey = (keyEvent) => {
      if (keyEvent.key === 'Escape') this.cancelDateSelect();
    };
    this._dateSelectDocClick = (clickEvent) =>
      this._onDateSelectDocumentClick(clickEvent);
    window.addEventListener('pointermove', this._dateSelectMove, {
      passive: false,
    });
    window.addEventListener('keydown', this._dateSelectKey);
    document.addEventListener('click', this._dateSelectDocClick, true);
    document.body.classList.add('calendar-date-selecting');
  },

  _moveDateSelect(event) {
    if (!this.dateSelect) return;
    const press = this._dateSelectPress;
    if (event.pointerType === 'touch') {
      if (!press || !press.held) return;
      if (!press.moved) {
        const dx = event.clientX - press.startX;
        const dy = event.clientY - press.startY;
        if (
          Math.abs(dx) < TOUCH_MOVE_THRESHOLD &&
          Math.abs(dy) < TOUCH_MOVE_THRESHOLD
        ) {
          return;
        }
        press.moved = true;
      }
    }
    if (event.cancelable) event.preventDefault();
    const stack =
      document.elementsFromPoint(event.clientX, event.clientY) || [];
    const cell = stack.find(
      (element) =>
        element.classList && element.classList.contains('calendar-date'),
    );
    if (!cell) return;
    this._applyDateSelectHover(this._readDateCell(cell));
  },

  _onDateSelectDocumentClick(event) {
    if (this._suppressDateSelectClick) return;
    if (!this.dateSelect) return;
    const cell = event.target?.closest?.('.calendar-date');
    if (
      cell &&
      String(cell.getAttribute('data-product-id')) ===
        String(this.dateSelect.productId)
    ) {
      return;
    }
    this.cancelDateSelect();
  },

  _applyDateSelectHover(info) {
    const sel = this.dateSelect;
    if (!sel || !info?.date || String(info.productId) !== String(sel.productId))
      return;

    const next = { ...sel, hoverDate: info.date };
    if (
      !sel.scheduled &&
      info.unitId &&
      this._productUnitIds(sel).includes(String(info.unitId))
    ) {
      next.hoverUnitId = info.unitId;
    }
    this.dateSelect = next;
    this._refreshDateSelectValidity();
  },

  _refreshDateSelectValidity() {
    const sel = this.dateSelect;
    if (!sel) return;

    const dates = this._selectedDates(sel);
    let invalid = dates.length === 0;

    if (sel.scheduled) {
      const tour = (this.tours || []).find(
        (item) => String(item.id) === String(sel.productId),
      );
      dates.forEach((date) => {
        const free = sel.anchorUnitId
          ? this.scheduledUnitDateAvailable(
              sel.anchorUnitId,
              sel.departureId,
              date,
            )
          : Boolean(tour) &&
            this.isScheduledDepartureAvailable(tour, sel.departureId, date);
        if (!free) invalid = true;
      });
    } else {
      const unitIds = this._selectedUnits(sel);
      if (!unitIds.length) invalid = true;
      dates.forEach((date) => {
        unitIds.forEach((unitId) => {
          if (!this._unscheduledCellFree(unitId, date)) invalid = true;
        });
      });
    }

    if (sel.invalid !== invalid) {
      this.dateSelect = { ...this.dateSelect, invalid };
    }
  },

  _unscheduledCellFree(unitId, date) {
    const rates = this.calendarDatesWithRates?.[unitId];
    if (!rates || !rates[date]) return false;
    if (this.calendarDatesBooked?.[unitId]?.[date]) return false;
    if (this._occupiedCellKeys().has(`${String(unitId)}|${date}`)) return false;
    const blocked = this.calendarDatesBlocked?.[unitId]?.dates?.[date];
    return !(blocked && blocked.length);
  },

  _productUnitIds(sel) {
    const products =
      sel.productType === 'Tour' ? this.tours : this.accommodations;
    const product = (products || []).find(
      (item) => String(item.id) === String(sel.productId),
    );
    if (!product) return [];
    const units =
      sel.productType === 'Tour'
        ? product.tour_units
        : product.accommodation_units;
    return (units || []).map((unit) => String(unit.id));
  },

  _paintedDates(sel) {
    const start =
      sel.anchorDate <= sel.hoverDate ? sel.anchorDate : sel.hoverDate;
    const end =
      sel.anchorDate <= sel.hoverDate ? sel.hoverDate : sel.anchorDate;
    return (this.calendar_dates || []).filter(
      (date) => date >= start && date <= end,
    );
  },

  _selectedDates(sel) {
    const dates = this._paintedDates(sel);
    // Accommodation cells are nights. The later click is the checkout day, so that
    // night is not part of the stay. A click on the 28th books through the 27th.
    if (sel.productType === 'Accommodation' && dates.length > 1) {
      return dates.slice(0, -1);
    }
    return dates;
  },

  _selectedUnits(sel) {
    if (sel.scheduled) {
      return sel.anchorUnitId ? [String(sel.anchorUnitId)] : [];
    }
    const ids = this._productUnitIds(sel);
    const start = ids.indexOf(String(sel.anchorUnitId));
    const end = ids.indexOf(String(sel.hoverUnitId || sel.anchorUnitId));
    if (start < 0 || end < 0) {
      return sel.anchorUnitId ? [String(sel.anchorUnitId)] : [];
    }
    const lower = Math.min(start, end);
    const upper = Math.max(start, end);
    return ids.slice(lower, upper + 1);
  },

  _dateSelectSnapshot() {
    const sel = this.dateSelect;
    const dates = this._selectedDates(sel);
    const unitIds = this._selectedUnits(sel);
    return {
      dates,
      productId: sel.productId,
      productType: sel.productType,
      unitIds,
      unitId: unitIds[0] || null,
      rateUnitId: sel.rateUnitId || unitIds[0] || null,
      departureId: sel.departureId,
      departureTime: sel.departureTime,
      scheduled: sel.scheduled,
    };
  },

  _readDateCell(cell) {
    if (!cell) return null;
    const unitId = cell.getAttribute('data-unit-id') || null;
    return {
      productId: cell.getAttribute('data-product-id'),
      productType: cell.getAttribute('data-product'),
      date: cell.getAttribute('data-date'),
      unitId,
      rateUnitId: unitId || cell.getAttribute('data-rate-unit-id') || null,
      departureId: cell.getAttribute('data-tour-departure-id') || null,
      departureTime: cell.getAttribute('data-tour-departure-time') || '',
    };
  },
};
